import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { FileUp, Loader2, FileText, AlertTriangle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtBytes } from "@/lib/api";

const L = ({ children }) => <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</div>;
const blank = (clientId) => ({ client_id: clientId || "", numero: "", data_emissione: format(new Date(), "yyyy-MM-dd"), importo: "", imponibile: "", iva_pct: "",
  scadenza: "", stato: "da_pagare", data_pagamento: "", note: "" });
const BILL = [["ragione_sociale", "Intestatario"], ["piva", "P.IVA"], ["codice_fiscale", "Codice fiscale"], ["sede", "Sede"],
  ["pec", "PEC"], ["sdi", "Codice SDI"], ["iban", "IBAN"], ["banca", "Banca"]];
const EMPTY_BILL = Object.fromEntries(BILL.map(([k]) => [k, ""]));

const BillingBlock = ({ value, clientId, onChange, onReload, loading }) => {
  const incomplete = !value.pec?.trim() && !value.sdi?.trim();
  return (
    <div className="rounded-2xl border border-[#5FB3EA] bg-[#A6DAFC]/60 p-4 space-y-3" data-testid="invoice-billing">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#E8173A]">Dati di fatturazione</div>
        <Button type="button" variant="ghost" size="sm" onClick={onReload} disabled={!clientId || loading} className="rounded-full text-xs font-bold" data-testid="invoice-billing-reload">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}Ricarica dall'anagrafica</Button>
      </div>
      {incomplete && clientId && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 border border-amber-300 px-3 py-2 text-xs font-bold text-[#0A1128]" data-testid="invoice-billing-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600" />Dati per fattura elettronica incompleti (manca PEC e codice SDI)
          <Link to={`/clienti/${clientId}?tab=anagrafica`} className="ml-auto text-[#E8173A] underline underline-offset-2" data-testid="invoice-billing-anagrafica-link">Completa l'anagrafica</Link>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {BILL.map(([k, l]) => (
          <div key={k} className={k === "ragione_sociale" || k === "sede" ? "col-span-2" : ""}><L>{l}</L>
            <Input value={value[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="rounded-xl bg-white" data-testid={`invoice-billing-${k}`} /></div>
        ))}
      </div>
      <p className="text-[11px] text-slate-600">I dati vengono salvati con la fattura: le modifiche successive all'anagrafica non cambiano le fatture già registrate.</p>
    </div>
  );
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));

export const InvoiceFormDialog = ({ open, onOpenChange, invoice, clientId, clients, onSaved }) => {
  const [f, setF] = useState(blank(clientId));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [bill, setBill] = useState(EMPTY_BILL);
  const [billLoading, setBillLoading] = useState(false);
  const ref = useRef();
  const loadBill = async (cid) => {
    if (!cid) return;
    setBillLoading(true);
    try { setBill((await api.get(`/fatture/dati-cliente/${cid}`)).data); } catch (err) { toast.error(errMsg(err)); }
    finally { setBillLoading(false); }
  };
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setF(invoice ? { ...blank(), ...Object.fromEntries(Object.entries(invoice).map(([k, v]) => [k, v ?? ""])) } : blank(clientId));
    if (invoice?.fatturazione) setBill({ ...EMPTY_BILL, ...invoice.fatturazione });
    else if (invoice || clientId) loadBill(invoice?.client_id || clientId);
    else setBill(EMPTY_BILL);
  }, [open, invoice, clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => setF((p) => {
    const n = { ...p, [k]: v?.target ? v.target.value : v };
    if (k === "client_id" && v !== p.client_id) loadBill(v);
    if ((k === "imponibile" || k === "iva_pct") && num(n.imponibile) !== null && num(n.iva_pct) !== null) n.importo = (num(n.imponibile) * (1 + num(n.iva_pct) / 100)).toFixed(2);
    return n;
  });
  const pick = (fl) => {
    if (!fl) return;
    if (!fl.name.toLowerCase().endsWith(".pdf")) return toast.error("Carica un file PDF");
    if (fl.size > 20 * 1024 * 1024) return toast.error("Il PDF supera i 20 MB");
    setFile(fl);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!f.client_id) return toast.error("Seleziona il cliente");
    setBusy(true);
    try {
      const body = { client_id: f.client_id, numero: f.numero, data_emissione: f.data_emissione, importo: num(f.importo), imponibile: num(f.imponibile), iva_pct: num(f.iva_pct),
        scadenza: f.scadenza || null, stato: f.stato, data_pagamento: f.stato === "pagata" ? (f.data_pagamento || null) : null, note: f.note, fatturazione: bill };
      let r = invoice ? await api.put(`/fatture/${invoice.id}`, body) : await api.post("/fatture", body);
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        try { r = await api.post(`/fatture/${r.data.id}/pdf`, fd); } catch (err) { toast.error(`Fattura salvata, ma PDF non caricato: ${errMsg(err)}`); }
      }
      toast.success(invoice ? "Fattura aggiornata" : "Fattura creata"); onSaved(r.data); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl core-scroll" data-testid="invoice-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{invoice ? `Fattura n. ${invoice.numero}` : "Nuova fattura"}</DialogTitle>
          <DialogDescription>Importi in euro. Il PDF è facoltativo e si può caricare anche in seguito.</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><L>Cliente *</L><Select value={f.client_id} onValueChange={set("client_id")} disabled={!!clientId && !invoice}>
              <SelectTrigger className="rounded-xl" data-testid="invoice-client"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent></Select></div>
            <div><L>Numero *</L><Input required value={f.numero} onChange={set("numero")} placeholder="Es. 12/2026" className="rounded-xl" data-testid="invoice-numero" /></div>
            <div><L>Data emissione *</L><Input required type="date" value={f.data_emissione} onChange={set("data_emissione")} className="rounded-xl" data-testid="invoice-data" /></div>
            <div><L>Scadenza</L><Input type="date" value={f.scadenza} onChange={set("scadenza")} className="rounded-xl" data-testid="invoice-scadenza" /></div>
            <div><L>Imponibile (€)</L><Input inputMode="decimal" value={f.imponibile} onChange={set("imponibile")} className="rounded-xl" data-testid="invoice-imponibile" /></div>
            <div><L>IVA %</L><Input inputMode="decimal" value={f.iva_pct} onChange={set("iva_pct")} placeholder="22" className="rounded-xl" data-testid="invoice-iva" /></div>
            <div><L>Importo totale (€) *</L><Input required inputMode="decimal" value={f.importo} onChange={set("importo")} className="rounded-xl font-bold" data-testid="invoice-importo" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><L>Stato</L><Select value={f.stato} onValueChange={set("stato")}><SelectTrigger className="rounded-xl" data-testid="invoice-stato"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="da_pagare">Da pagare</SelectItem><SelectItem value="pagata">Pagata</SelectItem></SelectContent></Select></div>
              {f.stato === "pagata" && <div><L>Pagata il</L><Input type="date" value={f.data_pagamento} onChange={set("data_pagamento")} className="rounded-xl" data-testid="invoice-data-pagamento" /></div>}
            </div>
          </div>
          <BillingBlock value={bill} clientId={f.client_id} onChange={setBill} onReload={() => loadBill(f.client_id)} loading={billLoading} />
          <div><L>Note</L><Textarea rows={2} value={f.note} onChange={set("note")} className="rounded-xl" data-testid="invoice-note" /></div>
          <div onClick={() => ref.current.click()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-5 py-5 text-center transition-colors ${drag ? "border-[#E8173A] bg-red-50/40" : "border-[#0A1128]/25 bg-[#A6DAFC] hover:border-[#E8173A]"}`} data-testid="invoice-pdf-dropzone">
            <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="invoice-pdf-input" />
            {file ? <div className="flex items-center justify-center gap-2 font-bold text-[#0A1128]"><FileText className="h-4 w-4 text-[#E8173A]" />{file.name} · {fmtBytes(file.size)}</div> : (
              <div className="text-sm text-[#0A1128]"><FileUp className="h-5 w-5 mx-auto mb-1" />Trascina qui il PDF della fattura o clicca (max 20 MB){invoice?.pdf && <div className="text-xs text-slate-600 mt-1">Attuale: {invoice.pdf.nome} – caricane uno nuovo per sostituirlo</div>}</div>
            )}
          </div>
          <DialogFooter><Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="invoice-cancel">Annulla</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="invoice-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Salva</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
